import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import * as React from "react";
import { setSelectManufacturer } from "../../../redux/reducer/features/Integration/DmsSetupSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../redux/reducer/rootReducer";
import { useGetMappedDmsManufacturersMutation } from "../../../redux/reducer/features/ShoppingPortal/ShoppingPortalSlice";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import ManufacturerGrid from "./Grids/ManufacturerGrid";
import { DmsManufacturer } from "../../../models/features/ShoppingPortal/DmsManufacturer";
import { ProductManufacturer } from "../../../models/features/Product/Product";
import ManufacturerDialog from "./Popup/ManufacturerDialog";
import { AppGridId } from "../../../models/app/AppGridModels";
import { GetAppGridColumns } from "../../../services/utilities/utility";
import AppConfirmDialog from "../../app/AppConfirmDialog";
import TruncatedCellWithTooltip from "../../common/TruncatedCellWithTooltip";

interface DmsManufacturersProps {
  handleNext: any;
  handleBack: any;
  catalogManufacturers: ProductManufacturer[];
  dmsType: number | undefined;
}

const DmsManufacturers: React.FunctionComponent<DmsManufacturersProps> = ({
  handleBack,
  handleNext,
  catalogManufacturers,
  dmsType,
}) => {
  const gridSetting = useSelector((state: AppState) =>
    state?.AppGridReducer?.setting.find(
      (x) => x.gridId === AppGridId.DMS_Manufacturers,
    ),
  );
  const [popup, setPopup] = React.useState(false);
  const [selectedRowData, setSelectedRowData] =
    React.useState<DmsManufacturer>();
  const [navigateOpen, setNavigateOpen] = React.useState(false);
  const [editingRowId, setEditingRowId] = React.useState<string | null>(null);
  const [editingValue, setEditingValue] = React.useState("");

  const selectedDmsManufacturers = useSelector(
    (state: AppState) => state.dmsReducer.dmsManufacturers,
  );
  const [wholegoodMfrOnly, setWholegoodMfrOnly] = React.useState(true);
  const [dmsManufacturers, setDmsManufacturers] = React.useState<
    DmsManufacturer[]
  >([]);
  const dispatch = useDispatch();
  const locationIds = useSelector(
    (state: AppState) => state.dmsReducer.locationIds,
  );

  const [dmsManufacturersMutation, dmsManufacturersMutationResult] =
    useGetMappedDmsManufacturersMutation();

  React.useEffect(() => {
    dmsManufacturersMutation({ dealerId: locationIds[0] });
  }, []);

  React.useEffect(() => {
    if (
      dmsManufacturersMutationResult.data &&
      dmsManufacturersMutationResult.data.entityList
    ) {
      setDmsManufacturers(
        dmsManufacturersMutationResult.data.entityList
          .filter((n) => n.isActive === true)
          .map((n) => ({
            ...n,
            displayName: n.displayName || n.name,
          })),
      );
    }
  }, [dmsManufacturersMutationResult]);

  const handleClick = (item: any) => {
    setSelectedRowData(item);
  };

  const handleUnSync = (manufacturer: DmsManufacturer) => {
    setDmsManufacturers(
      dmsManufacturers.map((n) =>
        n.manufacturerId === manufacturer?.manufacturerId
          ? { ...n, catalogManufacturer: undefined }
          : n,
      ),
    );
    let filteredMfr = selectedDmsManufacturers.filter(
      (n) => n.manufacturerId !== manufacturer.manufacturerId,
    );
    console.log(filteredMfr, "filteredMfr");
    dispatch(
      setSelectManufacturer({
        dmsManufacturers: filteredMfr,
      }),
    );
  };

  const handleCatalogManufacturerClick = (
    catalogManufacturer: ProductManufacturer,
  ) => {
    setDmsManufacturers(
      dmsManufacturers.map((n) =>
        n.manufacturerId === selectedRowData?.manufacturerId
          ? { ...n, catalogManufacturer: catalogManufacturer }
          : n,
      ),
    );
  };

  const columns: GridColDef[] = [
    {
      field: "code",
      headerName: "Dms Mfr Code",
      width: 180,
      renderCell: (params: GridRenderCellParams<any>) => {
        return <TruncatedCellWithTooltip value={params.row.code} />;
      },
    },
    {
      field: "name",
      headerName: "Dms Mfr Name",
      flex: 1,
      renderCell: (params: GridRenderCellParams<any>) => {
        return <TruncatedCellWithTooltip value={params.row.name} />;
      },
    },
    {
      field: "displayName",
      headerName: "Display Name",
      flex: 1,
      renderCell: (params: GridRenderCellParams<any>) => {
        const isEditing = editingRowId === params.row.manufacturerId;
        if (isEditing) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <TextField
                size="small"
                variant="standard"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                autoFocus
                fullWidth
                sx={{ mr: 0.5 }}
              />
              <IconButton
                size="small"
                color="success"
                onClick={() => {
                  setDmsManufacturers((prev) =>
                    prev.map((n) =>
                      n.manufacturerId === params.row.manufacturerId
                        ? { ...n, displayName: editingValue }
                        : n,
                    ),
                  );
                  setEditingRowId(null);
                }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => setEditingRowId(null)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        }
        return (
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <TruncatedCellWithTooltip value={params.row.displayName} />
            <IconButton
              size="small"
              onClick={() => {
                setEditingRowId(params.row.manufacturerId);
                setEditingValue(params.row.displayName || "");
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: "commonMfgId",
      headerName: "Common Mfr Id",
      width: 120,
    },
    {
      field: "manufacturer",
      headerName: "Catalog Mfr",
      flex: 1,
      renderCell: (params: GridRenderCellParams<any>) => {
        const manufacturer = params.row?.catalogManufacturer
          ? params.row?.catalogManufacturer?.shortTitle +
            " - " +
            params.row?.catalogManufacturer?.title +
            (params.row?.catalogManufacturer?.commonMfgId
              ? " (" + params.row?.catalogManufacturer?.commonMfgId + ")"
              : "")
          : "N/A";
        return <TruncatedCellWithTooltip value={manufacturer} />;
      },
    },
    {
      field: "Map",
      headerName: "Map Manually",
      type: "actions",

      renderCell: (params: GridRenderCellParams) => {
        return params.row?.catalogManufacturer ? (
          <Button
            disabled={params.row.isShopifySync}
            color="error"
            variant="contained"
            size="small"
            sx={{ fontSize: "0.7rem" }}
            onClick={() => {
              handleUnSync(params.row);
            }}
          >
            Unmap
          </Button>
        ) : (
          <Button
            disabled={params.row.isShopifySync}
            color="warning"
            variant="contained"
            size="small"
            sx={{ fontSize: "0.7rem" }}
            onClick={() => {
              handleClick(params.row);
              setPopup(true);
            }}
          >
            Map
          </Button>
        );
      },
    },
  ];

  const handleItemSelect = (items: any[]) => {
    const updatedSelectedManufacturers =
      selectedDmsManufacturers.filter((sp) =>
        items.includes(sp.manufacturerId),
      ) ?? [];

    dmsManufacturers.forEach((item) => {
      if (
        items.includes(item.manufacturerId) &&
        !selectedDmsManufacturers.some(
          (sp) => sp.manufacturerId === item.manufacturerId,
        )
      ) {
        updatedSelectedManufacturers.push(item);
      }
    });

    // Sync displayName from dmsManufacturers into selected list
    updatedSelectedManufacturers.forEach((m, i) => {
      const current = dmsManufacturers.find(
        (d) => d.manufacturerId === m.manufacturerId,
      );
      if (current) {
        updatedSelectedManufacturers[i] = {
          ...m,
          displayName: current.displayName,
        };
      }
    });

    let filteredMfr = [];

    if (dmsType && dmsType === 0) {
      filteredMfr = updatedSelectedManufacturers.map((m) => ({
        ...m,
        isWholegoodManufacturer: true,
        isPartManufacturer: true,
      }));
    } else {
      filteredMfr = updatedSelectedManufacturers;
    }

    dispatch(setSelectManufacturer({ dmsManufacturers: filteredMfr }));
  };

  return (
    <>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        p={0.5}
      >
        <Alert severity="info">
          Please Select the Manufacturer You Want To Sync Products To Shopify
          Website. Only Selected Manufacturer's Products will get match.
        </Alert>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setNavigateOpen(true);
            }}
          >
            Back
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleNext}
            disabled={selectedDmsManufacturers.length === 0}
          >
            Next
          </Button>
        </Box>
      </Box>
      <Box sx={{ width: "100%" }} textAlign="right">
        <FormControlLabel
          control={
            <Switch
              size="small"
              value={wholegoodMfrOnly}
              defaultChecked
              onChange={() => {
                setWholegoodMfrOnly(!wholegoodMfrOnly);
              }}
            />
          }
          label="Wholegood Mfr only"
        />
      </Box>
      <ManufacturerGrid
        data={
          wholegoodMfrOnly
            ? dmsManufacturers.filter((n) => n.isWholegoodManufacturer)
            : dmsManufacturers
        }
        isLoading={dmsManufacturersMutationResult.isLoading}
        columns={GetAppGridColumns(columns, gridSetting?.columnVisibilityModel)}
        selectedRows={selectedDmsManufacturers.map((n) => n.manufacturerId)}
        onSelect={(newSelection: any) => {
          handleItemSelect(newSelection);
        }}
      ></ManufacturerGrid>

      <ManufacturerDialog
        catalogManufacturers={catalogManufacturers}
        handleClose={() => {}}
        isOpen={popup}
        setPopup={setPopup}
        onSave={handleCatalogManufacturerClick}
        selectedDmsLocation={selectedRowData}
      ></ManufacturerDialog>
      <AppConfirmDialog
        headerText="Confirm Navigation"
        bodyText="Are you sure you want to go back? You will lose Locations mapping."
        onFail={() => {
          setNavigateOpen(false);
        }}
        onSuccess={() => {
          setNavigateOpen(false);
          handleBack();
        }}
        handleClose={() => {
          setNavigateOpen(false);
        }}
        isOpen={navigateOpen}
      />
    </>
  );
};

export default DmsManufacturers;
